/**
 * Which Slack photo belongs to a person on screen (Jeff, 2026-10-23). The desk meets people by
 * profile id (viewers, note authors), by email (holders) or only by name (a Slack list comment);
 * the lookup tries them in that order. Pure, so the index is tested without a network.
 */
import { type AvatarRow } from '@lsc/contract';
import { useSyncExternalStore } from 'react';

export interface AvatarIndex {
  byProfile: Map<string, string>;
  byEmail: Map<string, string>;
  byName: Map<string, string>;
}

const key = (s: string) => s.trim().toLowerCase();

export function avatarIndex(rows: AvatarRow[]): AvatarIndex {
  const ix: AvatarIndex = { byProfile: new Map(), byEmail: new Map(), byName: new Map() };
  for (const r of rows) {
    if (r.profileId) ix.byProfile.set(r.profileId, r.url);
    if (r.email) ix.byEmail.set(key(r.email), r.url);
    for (const n of r.names) if (!ix.byName.has(key(n))) ix.byName.set(key(n), r.url);
  }
  return ix;
}

export function avatarFor(ix: AvatarIndex | null, who: { profileId?: string | null | undefined; email?: string | null | undefined; name?: string | null | undefined }): string | null {
  if (!ix) return null;
  return (who.profileId ? ix.byProfile.get(who.profileId) : undefined) ?? (who.email ? ix.byEmail.get(key(who.email)) : undefined) ?? (who.name ? ix.byName.get(key(who.name)) : undefined) ?? null;
}

/**
 * The desk's one copy of the index, set by the root once `staff.avatars` arrives (and every hour
 * after). A store rather than a query hook, so a card renders its initials anywhere, tests
 * included, and the photos appear when the index lands.
 */
let current: AvatarIndex | null = null;
const listeners = new Set<() => void>();

export function setAvatars(rows: AvatarRow[] | null): void {
  current = rows ? avatarIndex(rows) : null;
  for (const fn of listeners) fn();
}

export function useAvatarIndex(): AvatarIndex | null {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
    () => current,
  );
}
